import { DEFAULT_BINDINGS, type GameAction } from '../../input/Input';
import { keyLabel } from '../../input/keyLabel';

/**
 * Key rebinding rules. Bindings are stored as the full action → code map in
 * `account.settings.bindings` (codes are `KeyboardEvent.code`, optionally
 * suffixed `+alt`). Binding a key that another action uses swaps the two, so
 * no action is ever left unbound.
 */

export type Bindings = Record<GameAction, string>;

/** Escape always opens settings / closes windows. */
export const LOCKED_ACTIONS: readonly GameAction[] = ['settings'];
export const RESERVED_CODES: readonly string[] = ['Escape'];
const MODIFIERS = new Set([
  'ShiftLeft',
  'ShiftRight',
  'ControlLeft',
  'ControlRight',
  'AltLeft',
  'AltRight',
  'MetaLeft',
  'MetaRight',
  'CapsLock',
  'Fn',
]);

export const ACTION_GROUPS: { name: string; actions: { id: GameAction; label: string }[] }[] = [
  {
    name: 'Combat',
    actions: [
      { id: 'skillQ', label: 'Skill 1' },
      { id: 'skillW', label: 'Skill 2' },
      { id: 'skillE', label: 'Skill 3' },
      { id: 'skillR', label: 'Skill 4' },
      { id: 'skillA', label: 'Skill 5' },
      { id: 'skillS', label: 'Skill 6' },
      { id: 'skillD', label: 'Skill 7' },
      { id: 'skillF', label: 'Skill 8' },
      { id: 'basic', label: 'Basic attack' },
      { id: 'dash', label: 'Dash' },
      { id: 'identityZ', label: 'Resonance 1' },
      { id: 'identityX', label: 'Resonance 2' },
      { id: 'awakening', label: 'Apex Art' },
    ],
  },
  {
    name: 'Items & world',
    actions: [
      { id: 'item1', label: 'Battle item 1' },
      { id: 'item2', label: 'Battle item 2' },
      { id: 'item3', label: 'Battle item 3' },
      { id: 'item4', label: 'Battle item 4' },
      { id: 'interact', label: 'Interact' },
      { id: 'mount', label: 'Mount' },
    ],
  },
  {
    name: 'Interface',
    actions: [
      { id: 'inventory', label: 'Bag' },
      { id: 'character', label: 'Character' },
      { id: 'skills', label: 'Skills' },
      { id: 'quests', label: 'Quest journal' },
      { id: 'map', label: 'World map' },
      { id: 'overlayMap', label: 'Overlay map' },
      { id: 'sigils', label: 'Sigils' },
      { id: 'cards', label: 'Relic cards' },
      { id: 'journal', label: 'Explorer’s Journal' },
      { id: 'bounties', label: 'Bounty Board' },
      { id: 'market', label: 'Market' },
      { id: 'bonds', label: 'Bonds' },
      { id: 'haven', label: 'Haven Isle' },
      { id: 'lifeHud', label: 'Life-skill HUD' },
      { id: 'emotes', label: 'Emotes' },
      { id: 'party', label: 'Party' },
      { id: 'chat', label: 'Chat' },
      { id: 'hideUi', label: 'Hide interface' },
      { id: 'settings', label: 'Settings / close' },
    ],
  },
];

const ACTIONS = Object.keys(DEFAULT_BINDINGS) as GameAction[];

/** Saved overrides merged over the defaults (unknown actions are dropped). */
export function fullBindings(saved: Record<string, string>): Bindings {
  const out = { ...DEFAULT_BINDINGS };
  for (const a of ACTIONS) {
    const v = saved[a];
    if (typeof v === 'string' && v) out[a] = v;
  }
  return out;
}

export type RebindResult =
  { ok: true; bindings: Bindings; swapped: GameAction | null } | { ok: false; reason: string };

export function rebind(current: Bindings, action: GameAction, code: string): RebindResult {
  if (LOCKED_ACTIONS.includes(action)) return { ok: false, reason: 'This key cannot be changed.' };
  if (RESERVED_CODES.includes(code.replace('+alt', '')))
    return { ok: false, reason: `${keyLabel(code)} is reserved.` };
  const prev = current[action];
  if (prev === code) return { ok: true, bindings: { ...current }, swapped: null };
  const next = { ...current, [action]: code };
  const other = ACTIONS.find((a) => a !== action && current[a] === code) ?? null;
  if (other) next[other] = prev;
  return { ok: true, bindings: next, swapped: other };
}

/** Code for a captured key press, or null for bare modifiers. */
export function codeFromEvent(e: { code: string; altKey: boolean }): string | null {
  if (!e.code || MODIFIERS.has(e.code)) return null;
  return e.altKey ? `${e.code}+alt` : e.code;
}

/** Codes bound to more than one action (only possible with hand-edited saves). */
export function conflicts(b: Bindings): Map<string, GameAction[]> {
  const by = new Map<string, GameAction[]>();
  for (const a of ACTIONS) by.set(b[a], [...(by.get(b[a]) ?? []), a]);
  for (const [k, v] of by) if (v.length < 2) by.delete(k);
  return by;
}
